import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { 
  FileText, 
  Wand2,
  Download,
  ChevronLeft,
  Loader2,
  CheckCircle2,
  Edit3,
  Save,
  RefreshCw,
  Copy,
  FileDown,
  Sparkles,
  BookOpen,
  Mail,
  ClipboardList
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";

// v1 base — kept for the proposal-generate / proposal-export endpoints
// that haven't been ported to v2 yet. RFP fetch uses v2Api.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Adapt v2 RfpOut into the shape this v1-era JSX expects. The JSX gates
// on rfp.status === "analyzed"; v2 uses parse_status === "ANALYZED".
// Per memory #8 (strangler-pattern adapter), normalise at the fetch
// boundary so the downstream JSX stays untouched.
const adaptRfpForProposal = (v2) => {
  if (!v2) return null;
  const ps = (v2.parse_status || "").toUpperCase();
  const v1Status = ps === "ANALYZED" ? "analyzed"
    : ps === "PARSING" ? "processing"
    : ps === "FAILED" ? "failed"
    : "uploaded";
  return { ...v2, status: v1Status };
};

// Adapt the v2 proposal payload into the v1 shape this page's JSX
// renders against. v2: {rfp_id, version, cover_letter, executive_summary,
// sections: [{key, title, content, edited, regenerated_at}]}.
// v1: {id, sections: [{id, section_title, suggested_response, status,
// word_count}]}.
const adaptV2Proposal = (v2p) => {
  if (!v2p) return null;
  return {
    // Use rfp_id as the proposal id — backend keys proposals by RFP,
    // not by a separate proposal id. JSX uses proposal.id only for
    // building action URLs, which we route via v2Api by rfp/key now.
    id: v2p.rfp_id,
    version: v2p.version || 1,
    generated_at: v2p.generated_at,
    cover_letter: v2p.cover_letter || "",
    executive_summary: v2p.executive_summary || "",
    sections: (v2p.sections || []).map((s) => ({
      id: s.key,                                  // legacy id field
      section_id: s.key,
      key: s.key,
      section_title: s.title,
      title: s.title,
      suggested_response: s.content || "",
      content: s.content || "",
      status: s.edited ? "in_progress" : "completed",
      word_count: (s.content || "").split(/\s+/).filter(Boolean).length,
      rfp_section_index: s.rfp_section_index,
      regenerated_at: s.regenerated_at,
      // Phase 2c (Library RAG): list of past-proposal titles whose
      // snippets were injected into this section's prompt. Empty array
      // when no Library matches were found.
      exemplar_refs: Array.isArray(s.exemplar_refs) ? s.exemplar_refs : [],
    })),
  };
};

const ProposalGenerator = ({ rfpId: rfpIdProp, embedded = false }) => {
  const params = useParams();
  const rfpId = rfpIdProp || params.rfpId;
  const navigate = useNavigate();
  const [rfp, setRfp] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [editingSection, setEditingSection] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [regeneratingSection, setRegeneratingSection] = useState(null);
  const [versions, setVersions] = useState([]);
  const [restoringVersion, setRestoringVersion] = useState(null);
  const [versionsOpen, setVersionsOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [rfpId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // RFP from v2 — the v1 /api/rfp/{id} route doesn't exist anymore,
      // which is why this page was previously showing "RFP must be fully
      // analyzed" on every tender, including analysed ones.
      const v2rfp = await v2Api.rfp.get(rfpId);
      setRfp(adaptRfpForProposal(v2rfp));
      
      // Try to get existing proposal (v2). 404 means none generated yet.
      try {
        const v2p = await v2Api.rfp.getProposal(rfpId);
        setProposal(adaptV2Proposal(v2p));
      } catch (e) {
        if (e?.response?.status !== 404) {
          console.warn("Proposal fetch failed:", e);
        }
        setProposal(null);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load RFP data");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateProposal = async () => {
    setGenerating(true);
    try {
      // v2: POST /rfp/{id}/proposal runs Gemini server-side and returns
      // the generated payload directly — no second GET needed.
      const v2p = await v2Api.rfp.generateProposal(rfpId);
      setProposal(adaptV2Proposal(v2p));
      toast.success(`Proposal generated (v${v2p?.version || 1})`);
      setActiveTab("sections");
    } catch (error) {
      console.error("Generation error:", error);
      const msg = error?.response?.data?.detail
        || error?.message
        || "Failed to generate proposal";
      toast.error(String(msg));
    } finally {
      setGenerating(false);
    }
  };

  const handleExportProposal = async () => {
    if (!proposal) return;
    setExporting(true);
    try {
      // v2 DOCX endpoint streams the file; fetch with bearer header.
      const url = v2Api.rfp.proposalDocxUrl(rfpId);
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      const safe = (rfp?.title || rfp?.name || "Response").replace(/[^a-z0-9_-]+/gi, "_").slice(0, 80);
      a.setAttribute("download", `Proposal_${safe}.docx`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success("Proposal exported");
    } catch (error) {
      console.error("Export error:", error);
      toast.error(`Export failed: ${error?.message || error}`);
    } finally {
      setExporting(false);
    }
  };

  const loadVersions = async () => {
    try {
      const data = await v2Api.rfp.listProposalVersions(rfpId);
      setVersions(Array.isArray(data) ? data : []);
    } catch (e) {
      // 404 means no archive yet — totally fine.
      if (e?.response?.status !== 404) {
        console.warn("versions list failed:", e);
      }
      setVersions([]);
    }
  };

  // Refresh versions list whenever a proposal lands (initial fetch,
  // after generate, after regenerate, after restore). Cheap GET; the
  // archive list is bounded by user history.
  useEffect(() => {
    if (rfpId && proposal) loadVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfpId, proposal?.version, proposal?.generated_at]);

  const handleRestoreVersion = async (version) => {
    if (!await confirm(`Restore v${version}? Current draft will be archived.`, { title: `Restore v${version}`, confirmLabel: "Restore", destructive: false })) return;
    setRestoringVersion(version);
    setVersionsOpen(false);
    try {
      const restored = await v2Api.rfp.restoreProposalVersion(rfpId, version);
      setProposal(adaptV2Proposal(restored));
      toast.success(`Restored v${version}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Restore failed";
      toast.error(String(msg));
    } finally {
      setRestoringVersion(null);
    }
  };

  const handleRegenerateSection = async (sectionId) => {
    setRegeneratingSection(sectionId);
    try {
      // v2: POST /rfp/{id}/proposal/sections/{key}/regenerate runs
      // Gemini on the single section using the same prompt + profile
      // the original generation used. Returns the full updated proposal.
      const v2p = await v2Api.rfp.regenerateProposalSection(rfpId, sectionId);
      setProposal(adaptV2Proposal(v2p));
      toast.success("Section regenerated");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Regenerate failed";
      toast.error(String(msg));
    } finally {
      setRegeneratingSection(null);
    }
  };

  const handleEditSection = (section) => {
    setEditingSection(section.id);
    setEditContent(section.suggested_response);
  };

  const handleSaveSection = async (sectionId) => {
    try {
      // v2: PATCH /rfp/{id}/proposal/sections/{key} body {content}.
      // section.id in the adapted shape is the v2 `key`.
      await v2Api.rfp.patchProposalSection(rfpId, sectionId, editContent);
      // Update local state
      setProposal(prev => ({
        ...prev,
        sections: prev.sections.map(s =>
          s.id === sectionId
            ? { ...s, suggested_response: editContent, status: 'in_progress', word_count: editContent.split(' ').length }
            : s
        )
      }));
      
      setEditingSection(null);
      toast.success("Section saved!");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save section");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getSectionTypeColor = (type) => {
    const colors = {
      technical: "bg-indigo-100 text-indigo-700",
      financial: "bg-emerald-100 text-emerald-700",
      legal: "bg-red-100 text-red-700",
      scope: "bg-amber-100 text-amber-700",
      eligibility: "bg-purple-100 text-purple-700",
      payment_terms: "bg-blue-100 text-blue-700"
    };
    return colors[type] || "bg-gray-100 text-gray-700";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "bg-emerald-100 text-emerald-700";
      case "in_progress": return "bg-amber-100 text-amber-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const completionPercentage = proposal?.sections?.length
    ? Math.round((proposal.sections.filter(s => s.status === 'completed' || s.status === 'in_progress').length / proposal.sections.length) * 100)
    : 0;

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? "py-12" : "p-8 min-h-screen"}`} data-testid="proposal-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }

  return (
    <div className={embedded ? "p-0" : "p-8"} data-testid="proposal-generator">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-4">
          {!embedded && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/tender/${rfpId}`)}
              className="mt-1"
              data-testid="back-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-extrabold text-[#0F172A] font-['Manrope'] tracking-tight flex items-center gap-3">
              <Wand2 className="w-7 h-7 text-[#007AFF]" />
              Proposal Generator
            </h1>
            <p className="text-[#64748B] mt-1">
              {rfp?.name || "RFP"} - AI-powered proposal template
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          {!proposal ? (
            <Button
              onClick={handleGenerateProposal}
              disabled={generating || rfp?.status !== "analyzed"}
              className="bg-[#007AFF] hover:bg-[#0066DD]"
              data-testid="generate-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Proposal
                </>
              )}
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleGenerateProposal}
                disabled={generating}
                data-testid="regenerate-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                Regenerate
              </Button>
              {versions.length > 0 && (
                <div className="relative">
                  <Button
                    variant="outline"
                    onClick={() => setVersionsOpen((v) => !v)}
                    data-testid="versions-btn"
                    disabled={restoringVersion !== null}
                  >
                    {restoringVersion !== null ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : null}
                    History ({versions.length})
                  </Button>
                  {versionsOpen && (
                    <div className="absolute right-0 top-10 z-30 w-80 max-h-96 overflow-y-auto rounded-md border bg-white shadow-lg text-xs"
                         onClick={(e) => e.stopPropagation()}>
                      <div className="px-3 py-2 border-b text-[10px] uppercase tracking-wide text-slate-500 font-medium">
                        Archived versions (newest first)
                      </div>
                      {versions.map((v) => (
                        <div key={v.version} className="px-3 py-2 border-b last:border-b-0 hover:bg-slate-50 flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-800">v{v.version}</p>
                            <p className="text-[10px] text-slate-500 truncate">
                              {v.generated_at?.slice(0, 16).replace("T", " ")}
                              {" · "}{v.section_count} section{v.section_count === 1 ? "" : "s"}
                              {v.has_cover ? " · cover" : ""}
                              {v.has_exec ? " · exec" : ""}
                            </p>
                          </div>
                          <Button
                            size="sm" variant="outline"
                            className="h-7 text-[11px] px-2"
                            disabled={restoringVersion !== null}
                            onClick={() => handleRestoreVersion(v.version)}
                          >
                            Restore
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <Button
                onClick={handleExportProposal}
                disabled={exporting}
                className="bg-[#0F172A] hover:bg-[#1E293B]"
                data-testid="export-proposal-btn"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileDown className="w-4 h-4 mr-2" />
                )}
                Export Word
              </Button>
            </>
          )}
        </div>
      </div>

      {!proposal ? (
        /* No Proposal Yet */
        <Card className="border-[#E2E8F0]" data-testid="no-proposal-card">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 bg-[#007AFF]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Wand2 className="w-10 h-10 text-[#007AFF]" />
            </div>
            <h2 className="text-xl font-bold text-[#0F172A] mb-2">
              Generate Your Proposal Template
            </h2>
            <p className="text-[#64748B] mb-6 max-w-md mx-auto">
              Our AI will analyze the RFP sections and generate a comprehensive proposal template 
              with suggested responses based on your company profile.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto mb-8">
              <div className="p-4 bg-[#F1F5F9] rounded-lg">
                <Mail className="w-6 h-6 text-[#007AFF] mx-auto mb-2" />
                <p className="text-sm font-medium">Cover Letter</p>
                <p className="text-xs text-[#64748B]">Auto-generated</p>
              </div>
              <div className="p-4 bg-[#F1F5F9] rounded-lg">
                <BookOpen className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Executive Summary</p>
                <p className="text-xs text-[#64748B]">AI-written</p>
              </div>
              <div className="p-4 bg-[#F1F5F9] rounded-lg">
                <ClipboardList className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Section Responses</p>
                <p className="text-xs text-[#64748B]">With guidance</p>
              </div>
            </div>

            <Button
              onClick={handleGenerateProposal}
              disabled={generating || rfp?.status !== "analyzed"}
              size="lg"
              className="bg-[#007AFF] hover:bg-[#0066DD]"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating Proposal...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate Proposal Template
                </>
              )}
            </Button>

            {rfp?.status !== "analyzed" && (
              <p className="text-sm text-amber-600 mt-4">
                RFP must be fully analyzed before generating a proposal
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Proposal Generated */
        <>
          {/* Progress Bar */}
          <Card className="border-[#E2E8F0] mb-6" data-testid="progress-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-[#0F172A]">Proposal Progress</span>
                  <Badge variant="outline">{proposal.sections?.length || 0} sections</Badge>
                </div>
                <span className="text-lg font-bold text-[#007AFF]">{completionPercentage}%</span>
              </div>
              <Progress value={completionPercentage} className="h-2" />
            </CardContent>
          </Card>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-[#F1F5F9] p-1">
              <TabsTrigger value="overview" data-testid="tab-overview">
                <FileText className="w-4 h-4 mr-2" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="cover" data-testid="tab-cover">
                <Mail className="w-4 h-4 mr-2" />
                Cover Letter
              </TabsTrigger>
              <TabsTrigger value="executive" data-testid="tab-executive">
                <BookOpen className="w-4 h-4 mr-2" />
                Executive Summary
              </TabsTrigger>
              <TabsTrigger value="sections" data-testid="tab-sections">
                <ClipboardList className="w-4 h-4 mr-2" />
                Sections ({proposal.sections?.length || 0})
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-[#E2E8F0]">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-[#007AFF]/10 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-[#007AFF]" />
                      </div>
                      <div>
                        <p className="text-xs text-[#64748B]">Total Sections</p>
                        <p className="text-2xl font-bold">{proposal.sections?.length || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-[#E2E8F0]">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-xs text-[#64748B]">AI Suggestions</p>
                        <p className="text-2xl font-bold">
                          {proposal.sections?.filter(s => s.suggested_response).length || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-[#E2E8F0]">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Edit3 className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-xs text-[#64748B]">Total Words</p>
                        <p className="text-2xl font-bold">
                          {proposal.sections?.reduce((acc, s) => acc + (s.word_count || 0), 0) || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Section Overview */}
              <Card className="border-[#E2E8F0]">
                <CardHeader>
                  <CardTitle className="font-['Manrope'] text-lg font-bold">
                    Section Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {proposal.sections?.map((section, idx) => (
                      <div 
                        key={section.id}
                        className="flex items-center justify-between p-3 bg-[#F1F5F9] rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-[#64748B] w-6">{idx + 1}.</span>
                          <span className="font-medium">{section.title}</span>
                          <Badge className={getSectionTypeColor(section.section_type)}>
                            {section.section_type?.replace('_', ' ')}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-[#64748B]">{section.word_count || 0} words</span>
                          <Badge className={getStatusColor(section.status)}>
                            {section.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Cover Letter Tab */}
            <TabsContent value="cover">
              <Card className="border-[#E2E8F0]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-['Manrope'] text-lg font-bold">
                    Cover Letter
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(proposal.cover_letter)}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="p-6 bg-[#F1F5F9] rounded-lg whitespace-pre-wrap font-serif leading-relaxed">
                    {proposal.cover_letter || "Cover letter will be generated..."}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Executive Summary Tab */}
            <TabsContent value="executive">
              <Card className="border-[#E2E8F0]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-['Manrope'] text-lg font-bold">
                    Executive Summary
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(proposal.executive_summary)}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="p-6 bg-[#F1F5F9] rounded-lg whitespace-pre-wrap leading-relaxed">
                    {proposal.executive_summary || "Executive summary will be generated..."}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Sections Tab */}
            <TabsContent value="sections">
              <ScrollArea className="h-[700px] pr-4">
                <div className="space-y-6">
                  {proposal.sections?.map((section, idx) => (
                    <Card 
                      key={section.id} 
                      className="border-[#E2E8F0]"
                      data-testid={`section-${section.id}`}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="font-['Manrope'] text-base font-bold flex items-center gap-3">
                              <span className="w-8 h-8 bg-[#007AFF] text-white rounded-lg flex items-center justify-center text-sm">
                                {idx + 1}
                              </span>
                              {section.title}
                            </CardTitle>
                            <div className="flex gap-2 mt-2">
                              <Badge className={getSectionTypeColor(section.section_type)}>
                                {section.section_type?.replace('_', ' ')}
                              </Badge>
                              <Badge className={getStatusColor(section.status)}>
                                {section.status}
                              </Badge>
                              <Badge variant="outline">{section.word_count || 0} words</Badge>
                              {(section.exemplar_refs || []).length > 0 && (
                                <Badge
                                  variant="outline"
                                  className="bg-purple-50 border-purple-200 text-purple-700"
                                  title={section.exemplar_refs.map(e => e.title).join(" · ")}
                                >
                                  Based on {section.exemplar_refs.length} past proposal{section.exemplar_refs.length === 1 ? "" : "s"}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyToClipboard(section.suggested_response)}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRegenerateSection(section.id)}
                              disabled={regeneratingSection === section.id}
                              title="Re-run Gemini on this single section"
                            >
                              {regeneratingSection === section.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Sparkles className="w-4 h-4 mr-1" />
                              )}
                              {regeneratingSection === section.id ? "" : "Regenerate"}
                            </Button>
                            {editingSection !== section.id ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditSection(section)}
                              >
                                <Edit3 className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => handleSaveSection(section.id)}
                                className="bg-emerald-600 hover:bg-emerald-700"
                              >
                                <Save className="w-4 h-4 mr-1" />
                                Save
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* RFP Requirement */}
                        <div>
                          <p className="text-xs font-medium text-[#64748B] mb-2">RFP REQUIREMENT</p>
                          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm italic">
                            {section.rfp_requirement || "No specific requirement extracted"}
                          </div>
                        </div>

                        {/* Guidance */}
                        <div>
                          <p className="text-xs font-medium text-[#64748B] mb-2">GUIDANCE</p>
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm whitespace-pre-wrap">
                            {section.guidance}
                          </div>
                        </div>

                        {/* Response */}
                        <div>
                          <p className="text-xs font-medium text-[#64748B] mb-2">
                            SUGGESTED RESPONSE
                            <span className="ml-2 text-[#007AFF]">
                              <Sparkles className="w-3 h-3 inline" /> AI Generated
                            </span>
                          </p>
                          {editingSection === section.id ? (
                            <Textarea
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              className="min-h-[200px] font-normal"
                              placeholder="Write your response..."
                            />
                          ) : (
                            <div className="p-4 bg-[#F1F5F9] rounded-lg text-sm whitespace-pre-wrap leading-relaxed">
                              {section.suggested_response || "Click 'Edit' to write your response"}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default ProposalGenerator;
